import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, TrendingUp, Calendar } from "lucide-react";

function StatCard({ label, value, sub, icon: Icon }) {
  return (
    <div className="bg-white rounded-3xl border border-zinc-100 p-6 flex items-start gap-4">
      <div className="w-11 h-11 rounded-2xl bg-[#FBE8EC] text-brand flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{label}</div>
        <div className="mt-1 font-display font-extrabold text-3xl tracking-tighter text-zinc-900">{value}</div>
        {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function Bar({ value, max }) {
  const pct = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 0;
  return (
    <div className="w-full h-2 rounded-full bg-zinc-100 overflow-hidden">
      <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("daily"); // 'daily' | 'monthly'
  const [days, setDays] = useState(30);
  const [months, setMonths] = useState(12);

  useEffect(() => {
    setLoading(true);
    api.get(`/adminX/analytics?days=${days}&months=${months}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [days, months]);

  const buckets = useMemo(() => (view === "daily" ? (data?.daily || []) : (data?.monthly || [])), [view, data]);
  const maxBucket = useMemo(() => buckets.reduce((m, b) => Math.max(m, b.total), 0), [buckets]);
  const bucketKey = view === "daily" ? "day" : "month";

  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  const monthStr = now.toISOString().slice(0, 7);
  const today = (data?.daily || []).find((d) => d.day === todayStr);
  const thisMonth = (data?.monthly || []).find((m) => m.month === monthStr);

  return (
    <div data-testid="admin-analytics" className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="All-time visits" value={loading ? "…" : (data?.total ?? 0).toLocaleString()} sub="Unique per visitor/page/day" icon={Users} />
        <StatCard label="Today" value={loading ? "…" : (today?.total ?? 0).toLocaleString()} sub={todayStr} icon={TrendingUp} />
        <StatCard label="This month" value={loading ? "…" : (thisMonth?.total ?? 0).toLocaleString()} sub={monthStr} icon={Calendar} />
      </div>

      <div className="bg-white rounded-3xl border border-zinc-100 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Visits</div>
            <div className="font-display font-bold text-xl text-zinc-900">Traffic over time</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-zinc-100 rounded-full p-1">
              <button data-testid="analytics-view-daily" onClick={() => setView("daily")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "daily" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-600"}`}>Daily</button>
              <button data-testid="analytics-view-monthly" onClick={() => setView("monthly")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "monthly" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-600"}`}>Monthly</button>
            </div>
            {view === "daily" ? (
              <select data-testid="analytics-days-select" value={days} onChange={(e) => setDays(Number(e.target.value))} className="text-sm bg-zinc-50 border border-zinc-200 rounded-full px-4 py-1.5 font-semibold text-zinc-700">
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            ) : (
              <select data-testid="analytics-months-select" value={months} onChange={(e) => setMonths(Number(e.target.value))} className="text-sm bg-zinc-50 border border-zinc-200 rounded-full px-4 py-1.5 font-semibold text-zinc-700">
                <option value={3}>Last 3 months</option>
                <option value={6}>Last 6 months</option>
                <option value={12}>Last 12 months</option>
                <option value={24}>Last 24 months</option>
              </select>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-zinc-500">Loading…</div>
        ) : buckets.length === 0 ? (
          <div className="py-10 text-center text-sm text-zinc-500">No visits recorded in this range yet.</div>
        ) : (
          <div className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">{view === "daily" ? "Day" : "Month"}</TableHead>
                  <TableHead className="w-24">Visits</TableHead>
                  <TableHead>Trend</TableHead>
                  <TableHead>Top pages</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {buckets.slice().reverse().map((b) => (
                  <TableRow key={b[bucketKey]} data-testid={`analytics-row-${b[bucketKey]}`}>
                    <TableCell className="font-mono text-sm text-zinc-900 whitespace-nowrap">{b[bucketKey]}</TableCell>
                    <TableCell className="font-semibold">{b.total.toLocaleString()}</TableCell>
                    <TableCell><Bar value={b.total} max={maxBucket} /></TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5">
                        {(b.pages || []).slice(0, 6).map((p) => (
                          <span key={p.path} className="inline-flex items-center gap-1.5 bg-zinc-100 rounded-full text-xs px-2.5 py-1">
                            <span className="font-mono text-zinc-700 truncate max-w-[220px]">{p.path}</span>
                            <span className="font-bold text-brand">{p.count}</span>
                          </span>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Top pages all-time */}
      <div className="bg-white rounded-3xl border border-zinc-100 p-6">
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">All-time top pages</div>
        <div className="font-display font-bold text-xl text-zinc-900 mb-4">Most-visited paths</div>
        {loading ? (
          <div className="py-6 text-center text-sm text-zinc-500">Loading…</div>
        ) : (data?.top_pages || []).length === 0 ? (
          <div className="py-6 text-center text-sm text-zinc-500">No pages tracked yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">#</TableHead>
                <TableHead>Path</TableHead>
                <TableHead className="w-32 text-right">Visits</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.top_pages.map((p, i) => (
                <TableRow key={p.path} data-testid={`top-page-row-${i}`}>
                  <TableCell className="text-zinc-500">{i + 1}</TableCell>
                  <TableCell className="font-mono text-sm text-zinc-900">{p.path}</TableCell>
                  <TableCell className="text-right font-semibold">{p.count.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
